import React, { useMemo, useState, useEffect, useRef } from 'react';
import { CalendarEvent, DragState } from '../../types/calendar';
import EventCard from './EventCard';
import { useNavigate } from 'react-router-dom';
import { FiTrash2 } from 'react-icons/fi';

interface DayViewProps {
  currentDate: Date;
  events: CalendarEvent[];
  dragState: DragState;
  onDragStart: (event: CalendarEvent, position: { x: number; y: number }) => void;
  onDragMove: (position: { x: number; y: number }) => void;
  onDragEnd: () => void;
  onTimeSlotDoubleClick: (date: Date) => void;
  onEventClick: (event: CalendarEvent) => void;
  businessOpenTime: string;
  businessCloseTime: string;
  currentTime?: Date;
  dragPreviewEvent?: CalendarEvent | null;
  dayColumnRef?: React.RefObject<HTMLDivElement>;
  firstEventRef?: React.RefObject<HTMLDivElement>;
  onTimeSlotClick?: (date: Date) => void;
  onDeleteAppointment?: (id: string) => void;
  onAddBreak?: (startHour: number) => void;
  onRemoveBreak?: (startTime: string, endTime: string) => void; // חדש
  isSpecialDay?: boolean;
  specialNote?: string;
  isClosed?: boolean;
  breaks?: { startTime: string; endTime: string }[];
}

const DEFAULT_CELL_HEIGHT = 80;
const MIN_ZOOM = 0.7;
const MAX_ZOOM = 2;

/** map slider [-2..2] <-> zoom [0.7..2] */
const sliderToZoom = (v: number) => {
  const t = (v + 2) / 4; // [0..1]
  return MIN_ZOOM + t * (MAX_ZOOM - MIN_ZOOM);
};
const zoomToSlider = (z: number) => {
  const t = (z - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM);
  return t * 4 - 2;
};

const DayView: React.FC<DayViewProps> = ({
  currentDate,
  events,
  dragState,
  onDragStart,
  onDragMove,
  onDragEnd,
  onTimeSlotDoubleClick,
  onEventClick,
  dayColumnRef,
  businessOpenTime,
  businessCloseTime,
  currentTime,
  dragPreviewEvent,
  firstEventRef,
  onTimeSlotClick,
  onDeleteAppointment,
  onAddBreak, // חדש
  onRemoveBreak, // חדש
  isSpecialDay,
  specialNote,
  isClosed,
  breaks = [],
}) => {
  const navigate = useNavigate();
  const [showChoice, setShowChoice] = useState<{ open: boolean; hour: number | null }>({ open: false, hour: null });

  /** Zoom */
  const [zoom, setZoom] = useState(() => {
    const saved = localStorage.getItem('dayview-zoom');
    return saved ? parseFloat(saved) : 1;
  });
  const pinchRef = useRef<HTMLDivElement>(null);

  /** Zoom modal */
  const [zoomModalOpen, setZoomModalOpen] = useState(false);
  const previousZoomRef = useRef(zoom);
  const [draftSlider, setDraftSlider] = useState<number>(zoomToSlider(1));

  // הודעה לבר התחתון שה-zoom modal נפתח/נסגר
  useEffect(() => {
    if (zoomModalOpen) {
      window.dispatchEvent(new CustomEvent('ui:appointment-modal', { detail: { open: true } }));
    } else {
      window.dispatchEvent(new CustomEvent('ui:appointment-modal', { detail: { open: false } }));
    }
  }, [zoomModalOpen]);

  /** --- Robust mobile pinch + two-finger detect --- */
  useEffect(() => {
    let initialDistance: number | null = null;
    let initialZoom = zoom;
    let pinchActive = false;
    let twoFingerTimer: number | null = null;

    const openModalIfNeeded = () => {
      // init slider to current zoom and open
      setDraftSlider(zoomToSlider(zoom));
      setZoomModalOpen(true);
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        // keep previous value for cancel
        previousZoomRef.current = zoom;

        // slight delay to avoid accidental two taps
        if (twoFingerTimer) window.clearTimeout(twoFingerTimer);
        twoFingerTimer = window.setTimeout(() => {
          openModalIfNeeded();
        }, 80);

        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        initialDistance = Math.hypot(dx, dy);
        initialZoom = zoom;
        pinchActive = true;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && initialDistance) {
        // prevent scroll capturing the gesture on iOS
        e.preventDefault();

        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const distance = Math.hypot(dx, dy);
        const scale = distance / initialDistance;
        const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, initialZoom * scale));
        setZoom(newZoom);                 // live preview
        setDraftSlider(zoomToSlider(newZoom)); // keep slider in sync while pinching
      }
    };

    const handleTouchEnd = (_e: TouchEvent) => {
      if (twoFingerTimer) {
        window.clearTimeout(twoFingerTimer);
        twoFingerTimer = null;
      }
      if (pinchActive) {
        pinchActive = false;
        // keep modal open (already opened on start) to let the user save/cancel
        initialDistance = null;
      }
    };

    const el = pinchRef.current;
    if (el) {
      el.addEventListener('touchstart', handleTouchStart, { passive: false });
      el.addEventListener('touchmove', handleTouchMove, { passive: false });
      el.addEventListener('touchend', handleTouchEnd, { passive: false });
      el.addEventListener('touchcancel', handleTouchEnd, { passive: false });
    }
    return () => {
      if (el) {
        el.removeEventListener('touchstart', handleTouchStart as any);
        el.removeEventListener('touchmove', handleTouchMove as any);
        el.removeEventListener('touchend', handleTouchEnd as any);
        el.removeEventListener('touchcancel', handleTouchEnd as any);
      }
      if (twoFingerTimer) {
        window.clearTimeout(twoFingerTimer);
      }
    };
  }, [zoom]);

  const CELL_HEIGHT = DEFAULT_CELL_HEIGHT * zoom;
  const hours = Array.from({ length: 24 }, (_, i) => i);

  const dayEvents = useMemo(
    () => events.filter(e => new Date(e.startTime).toDateString() === currentDate.toDateString()),
    [events, currentDate]
  );

  const getEventPosition = (event: CalendarEvent) => {
    const h = event.startTime.getHours();
    const m = event.startTime.getMinutes();
    return h * CELL_HEIGHT + (m / 60) * CELL_HEIGHT;
  };

  const getEventHeight = (event: CalendarEvent) => {
    const durationMinutes = (event.endTime.getTime() - event.startTime.getTime()) / (1000 * 60);
    const exactHeight = (durationMinutes / 60) * CELL_HEIGHT;
    return exactHeight;
  };

  const handleTimeSlotDoubleClickLocal = (hour: number) => {
    const slotDate = new Date(currentDate);
    slotDate.setHours(hour, 0, 0, 0);
    onTimeSlotDoubleClick(slotDate);
  };

  const handleTimeSlotClickLocal = (hour: number) => {
    setShowChoice({ open: true, hour });
  };

  const handleChoice = (type: 'event' | 'appointment' | 'break') => {
    if (showChoice.hour === null) return;
    const slotDate = new Date(currentDate);
    slotDate.setHours(showChoice.hour, 0, 0, 0);
    setShowChoice({ open: false, hour: null });
    if (type === 'appointment') {
      window.dispatchEvent(
        new CustomEvent('ui:open-new-appointment', {
          detail: { initialDate: slotDate, fromCalendar: true }
        })
      );
    }
    if (type === 'break' && onAddBreak) {
      onAddBreak(showChoice.hour);
    }
  };

  const getTimeInMinutes = (timeStr: string | undefined) => {
    if (!timeStr || typeof timeStr !== 'string') return undefined;
    const [hs, ms] = timeStr.trim().split(':');
    const h = Number(hs), m = Number(ms);
    if (isNaN(h) || isNaN(m)) return undefined;
    return h * 60 + m;
  };

  const totalHeight = 24 * CELL_HEIGHT;
  const openMinutes = getTimeInMinutes(businessOpenTime);
  const closeMinutes = getTimeInMinutes(businessCloseTime);
  const earlyHeight = openMinutes ? (openMinutes / 60) * CELL_HEIGHT : 0;
  const lateHeight = closeMinutes && closeMinutes < 24 * 60 ? totalHeight - (closeMinutes / 60) * CELL_HEIGHT : 0;

  const now = currentTime || new Date();
  const [nowState, setNowState] = useState(now);
  useEffect(() => {
    const t = setInterval(() => setNowState(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const breakOverlays = useMemo(() => {
    if (!breaks?.length) return null;
    return breaks.map((brk, idx) => {
      const s = getTimeInMinutes(brk.startTime);
      const e = getTimeInMinutes(brk.endTime);
      if (s == null || e == null) return null;
      const top = (s / 60) * CELL_HEIGHT;
      const height = ((e - s) / 60) * CELL_HEIGHT;
      return (
        <div
          key={idx}
          className="absolute w-full"
          style={{
            top,
            height,
            background: 'repeating-linear-gradient(45deg, #fef3c7 0px, #fef3c7 10px, #fde68a 10px, #fde68a 20px)',
            pointerEvents: 'auto',
            zIndex: 25,
            opacity: 0.6,
            borderTop: '1px solid #fbbf24',
            borderBottom: '1px solid #fbbf24',
            cursor: 'not-allowed',
            transition: 'background 0.3s'
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 8,
              left: '50%',
              transform: 'translateX(-50%)',
              color: '#92400e',
              fontWeight: 600,
              fontSize: 12,
              letterSpacing: '0.02em',
              opacity: 0.9,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              pointerEvents: 'auto'
            }}
          >
            הפסקה
            {onRemoveBreak && (
              <button
                style={{
                  marginRight: 8,
                  marginLeft: 4,
                  background: 'none',
                  border: 'none',
                  color: '#b91c1c',
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: 'pointer',
                  padding: 0,
                  opacity: 0.7,
                  pointerEvents: 'auto' // הפוך את הכפתור לפעיל תמיד
                }}
                title="הסר הפסקה"
                onClick={e => {
                  e.stopPropagation();
                  onRemoveBreak(brk.startTime, brk.endTime);
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>
      );
    });
  }, [breaks, CELL_HEIGHT, onRemoveBreak]);

  // Helper: is hour inside any break
  const isInBreak = (hour: number, minute: number = 0) => {
    if (!breaks || breaks.length === 0) return false;
    const slotMinutes = hour * 60 + minute;
    return breaks.some(brk => {
      const start = getTimeInMinutes(brk.startTime);
      const end = getTimeInMinutes(brk.endTime);
      return start != null && end != null && slotMinutes >= start && slotMinutes < end;
    });
  };

  /** Zoom modal actions */
  const closeZoomModal = () => {
    setZoom(previousZoomRef.current); // cancel → back to previous
    setZoomModalOpen(false);
  };
  const saveZoomModal = () => {
    localStorage.setItem('dayview-zoom', zoom.toString());
    setZoomModalOpen(false);
  };

  const resetZoom = () => {
    const defaultZoom = 1;
    setZoom(defaultZoom);
    setDraftSlider(zoomToSlider(defaultZoom));
  };

  const quickZoom = (direction: 'in' | 'out') => {
    const step = 0.2;
    const newZoom = direction === 'in'
      ? Math.min(MAX_ZOOM, zoom + step)
      : Math.max(MIN_ZOOM, zoom - step);
    setZoom(newZoom);
    setDraftSlider(zoomToSlider(newZoom));
  };

  return (
    <div
      className="flex-1 bg-white overflow-hidden"
      style={{
        position: 'relative',
        background: '#ffffff',
        minHeight: '100vh',
        height: '100%',
        boxShadow: 'none',
        marginTop: '0vh'
      }}
    >
      {/* Special day banner */}
      {isSpecialDay && (
        <div className="w-full py-2 px-4 bg-green-100 border-b border-green-300 flex items-center justify-center">
          <span className="text-green-700 font-bold text-base">יום מיוחד (עדיפות ביומן)</span>
        </div>
      )}

      {/* Closed day overlay */}
      {isClosed && (
        <div
          className="absolute w-full h-full"
          style={{
            top: 0,
            left: 0,
            background: 'repeating-linear-gradient(135deg, #f3f4f6 0px, #f3f4f6 8px, #e5e7eb 8px, #e5e7eb 16px)',
            pointerEvents: 'auto',
            zIndex: 30,
            backdropFilter: 'blur(2px) saturate(1.1)',
            WebkitBackdropFilter: 'blur(2px) saturate(1.1)',
            boxShadow: '0 2px 16px 0 #e0e7ef30'
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: '#b91c1c',
              fontWeight: 600,
              fontSize: 16,
              opacity: 0.8,
              pointerEvents: 'none'
            }}
          >
            יום סגור - אין שעות פעילות
          </div>
        </div>
      )}

      {/* Body */}
      <div
        className="flex-1 flex overflow-hidden"
        ref={pinchRef}
        // חשוב: מקל על לכידת pinch מבלי להרוס גלילה אנכית
        style={{ touchAction: dragState.isDragging ? 'none' : 'pan-y' }}
      >
        <div
          className="flex w-full"
          style={{
            height: '100%',
            WebkitOverflowScrolling: 'touch',
            position: 'relative',
            background: 'transparent',
            overflowY: 'auto',
            paddingBottom: 0,
            paddingTop: 0
          }}
        >
          {/* Time column */}
          <div className="w-16 border-r border-gray-200 bg-gray-50 flex-shrink-0 z-10 relative">
            <div style={{ height: `${CELL_HEIGHT * 24 + 1}px` }}>
              {hours.map(hour => (
                <div key={hour} className="border-b border-gray-100 flex items-start justify-center pt-1" style={{ height: `${CELL_HEIGHT}px` }}>
                  <span className="text-xs text-gray-600 font-normal">{hour.toString().padStart(2, '0')}:00</span>
                </div>
              ))}
            </div>
          </div>

          {/* Day column */}
          <div className="flex-1 relative" style={{ background: 'transparent' }} ref={dayColumnRef}>
            <style>{`
              .flex-1::-webkit-scrollbar { width: 8px; background: transparent; }
              .flex-1::-webkit-scrollbar-thumb {
                background: #dadce0;
                border-radius: 8px;
                min-height: 40px;
              }
              .flex-1::-webkit-scrollbar-thumb:hover {
                background: #bdc1c6;
              }
              .flex-1::-webkit-scrollbar-track { background: transparent; }
            `}</style>

            <div style={{ height: `${CELL_HEIGHT * 24 + 1}px`, overflow: 'visible' }}>
              {/* off-hours overlays */}
              {earlyHeight > 0 && (
                <div
                  className="absolute w-full"
                  style={{
                    top: 0,
                    height: `${earlyHeight}px`,
                    background: 'repeating-linear-gradient(135deg, #f3f4f6 0px, #f3f4f6 8px, #e5e7eb 8px, #e5e7eb 16px)',
                    pointerEvents: 'auto',
                    zIndex: 20,
                    borderBottom: '1px solid #e5e7eb',
                    cursor: 'not-allowed',
                    backdropFilter: 'blur(2px) saturate(1.1)',
                    WebkitBackdropFilter: 'blur(2px) saturate(1.1)',
                    boxShadow: '0 2px 16px 0 #e0e7ef30',
                    transition: 'background 0.3s'
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 8,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      color: '#b91c1c',
                      fontWeight: 600,
                      fontSize: 13,
                      letterSpacing: '0.03em',
                      opacity: 0.7,
                      pointerEvents: 'none'
                    }}
                  >
                    מחוץ לשעות פעילות (לפני פתיחה)
                  </div>
                </div>
              )}
              {lateHeight > 0 && (
                <div
                  className="absolute w-full"
                  style={{
                    top: `${totalHeight - lateHeight}px`,
                    height: `${lateHeight}px`,
                    background: 'repeating-linear-gradient(135deg, #f3f4f6 0px, #f3f4f6 8px, #e5e7eb 8px, #e5e7eb 16px)',
                    pointerEvents: 'auto',
                    zIndex: 20,
                    borderTop: '1px solid #e5e7eb',
                    cursor: 'not-allowed',
                    backdropFilter: 'blur(2px) saturate(1.1)',
                    WebkitBackdropFilter: 'blur(2px) saturate(1.1)',
                    boxShadow: '0 -2px 16px 0 #e0e7ef30',
                    transition: 'background 0.3s'
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 8,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      color: '#b91c1c',
                      fontWeight: 600,
                      fontSize: 13,
                      letterSpacing: '0.03em',
                      opacity: 0.7,
                      pointerEvents: 'none'
                    }}
                  >
                    מחוץ לשעות פעילות (אחרי סגירה)
                  </div>
                </div>
              )}

              {/* current hour highlight + red line - Google Calendar style */}
              {nowState.toDateString() === currentDate.toDateString() && (
                <>
                  {/* Red current time line */}
                  <div
                    className="absolute left-0 right-0 pointer-events-none z-30"
                    style={{ top: `${((nowState.getHours() * 60 + nowState.getMinutes()) / 60) * CELL_HEIGHT}px` }}
                  >
                    <div className="absolute -translate-y-1/2 left-0 right-0 flex items-center">
                      {/* Red circle */}
                      <div className="w-3 h-3 rounded-full bg-[#ea4335] -ml-1.5 shadow-md" />
                      {/* Red line */}
                      <div className="flex-1 h-[2px] bg-[#ea4335]" />
                    </div>
                  </div>
                </>
              )}

              {/* time slots */}
       {hours.map(hour => (
  <div key={hour} className="relative">
    <div
      className={`border-b border-gray-100 transition-colors ${
        isInBreak(hour) ? 'cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'
      }`}
      style={{ height: `${CELL_HEIGHT}px` }}
      onDoubleClick={isInBreak(hour) ? undefined : () => handleTimeSlotDoubleClickLocal(hour)}
      onClick={isInBreak(hour) ? undefined : () => handleTimeSlotClickLocal(hour)}
      title={isInBreak(hour) ? 'הפסקה - לא ניתן לקבוע תור' : undefined}
    >
      {isInBreak(hour) && (
        <span className="absolute left-2 top-2 text-yellow-700 text-xs font-bold">הפסקה</span>
      )}
    </div>
    {[1, 2, 3].map(quarter => (
      <div key={quarter} className="absolute w-full border-b border-gray-50" style={{ top: `${(quarter * CELL_HEIGHT) / 4}px`, height: 1 }} />
    ))}
  </div>
))}

              {/* events */}
              <div className="absolute top-0 left-0 right-0 z-20">
                {dayEvents.map((event, idx) => {
                  const eventTop = getEventPosition(event);
                  const eventHeight = getEventHeight(event);
                  let adjustedTop = eventTop;
                  if (dragState.isDragging && dragState.draggedEvent?.id === event.id) {
                    const dragDiff = dragState.currentPosition.y - dragState.startPosition.y;
                    adjustedTop += dragDiff;
                  }
                  const canDelete =
                    (event.status && event.status === 'canceled') || (event.color && event.color === '#EF4444');

                  // חישוב חפיפות
                  const overlapping = dayEvents.filter(other =>
                    other.id !== event.id &&
                    event.startTime < other.endTime &&
                    event.endTime > other.startTime
                  );
                  const overlapGroup = [event, ...overlapping].sort((a, b) => {
                    if (a.startTime.getTime() !== b.startTime.getTime()) {
                      return a.startTime.getTime() - b.startTime.getTime();
                    }
                    return a.id.localeCompare(b.id);
                  });
                  const overlapIndex = overlapGroup.findIndex(e => e.id === event.id);
                  const totalOverlaps = overlapGroup.length;

                  let leftStyle: string | number = 0;
                  let rightStyle: string | number = 0;
                  let widthStyle: string | undefined = undefined;

                  if (totalOverlaps > 1) {
                    const widthPercent = 100 / totalOverlaps;
                    leftStyle = `${overlapIndex * widthPercent}%`;
                    widthStyle = `${widthPercent}%`;
                    rightStyle = 'auto';
                  }

                  return (
                    <div
                      key={event.id}
                      ref={idx === 0 && firstEventRef ? firstEventRef : undefined}
                      className="absolute"
                      style={{
                        top: adjustedTop,
                        left: leftStyle,
                        right: rightStyle === 'auto' ? undefined : rightStyle,
                        width: widthStyle,
                        height: eventHeight
                      }}
                    >
                      <EventCard
                        event={event}
                        onDragStart={onDragStart}
                        onDragMove={onDragMove}
                        onDragEnd={onDragEnd}
                        onEventClick={onEventClick}
                        isDragging={dragState.isDragging && dragState.draggedEvent?.id === event.id}
                      />
                      {canDelete && onDeleteAppointment && (
                        <div
                          className="flex items-center gap-1 z-40"
                          style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'auto' }}
                        >
                          {event.title?.includes('בוטל') && <span className="text-xs text-red-500 font-semibold">בוטל</span>}
                          <button
                            className="bg-transparent p-0 m-0 flex items-center justify-center hover:bg-transparent"
                            title="מחק תור"
                            style={{ outline: 'none', cursor: 'pointer', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onClick={() => onDeleteAppointment(event.id)}
                          >
                            <FiTrash2 size={18} className="text-gray-700 hover:text-red-500 transition-colors" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
                {dayEvents.length === 0 && (
                  <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 text-center text-gray-300 text-sm select-none pointer-events-none">
                    אין תורים ליום זה
                  </div>
                )}
              </div>

              {breakOverlays}
            </div>
          </div>
        </div>
      </div>

      {/* slot action modal */}
      {showChoice.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div
            className="bg-white rounded-2xl shadow-xl p-6 flex flex-col gap-4 min-w-[260px] border border-gray-200"
            style={{ marginTop: 80, maxHeight: 'calc(100vh - 100px)', overflowY: 'auto', width: '90vw', maxWidth: 340 }}
          >
            <div className="flex flex-col items-center gap-2 mb-2">
              <div className="text-xl font-semibold text-gray-900 text-center">בחירת פעולה</div>
              <div className="text-sm text-gray-600 text-center">בחר מה תרצה לעשות בשעה זו</div>
            </div>
            <button
              className="w-full py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 shadow-sm transition-colors text-base flex items-center justify-center gap-2"
              onClick={() => handleChoice('appointment')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              תור חדש
            </button>
            <button
              className="w-full py-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 font-semibold hover:bg-amber-100 transition-colors text-base flex items-center justify-center gap-2"
              onClick={() => handleChoice('break')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              הוספת הפסקה
            </button>
            <button
              className="w-full py-3 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-colors text-base"
              onClick={() => setShowChoice({ open: false, hour: null })}
            >
              ביטול
            </button>
          </div>
        </div>
      )}

      {/* Zoom modal (live preview) */}
      {zoomModalOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm">
          <div
            className="w-full sm:w-auto bg-white rounded-t-3xl sm:rounded-2xl shadow-xl p-6 border border-gray-200"
            style={{ width: '100%', maxWidth: 480 }}
          >
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <span className="text-lg font-semibold text-gray-900">שינוי גודל התצוגה</span>
                <span className="px-2.5 py-1 rounded-full text-sm bg-blue-50 text-blue-700 border border-blue-200 font-medium">
                  {(zoom * 100).toFixed(0)}%
                </span>
              </div>
              <button aria-label="סגירה" onClick={closeZoomModal} className="text-gray-500 hover:text-gray-700 text-2xl leading-none">×</button>
            </div>

            <div className="flex items-center gap-3 mb-4">
              <button
                onClick={() => quickZoom('out')}
                className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 active:bg-gray-300 text-gray-700 font-bold text-xl flex items-center justify-center transition-colors"
                disabled={zoom <= MIN_ZOOM}
              >
                −
              </button>

              <div className="flex-1">
                <input
                  type="range"
                min={-2}
                max={2}
                step={0.1}
                value={draftSlider}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setDraftSlider(v);
                  setZoom(sliderToZoom(v));
                }}
                  className="w-full accent-blue-600 cursor-grab active:cursor-grabbing h-2"
                  style={{
                    background: `linear-gradient(to right, #dbeafe 0%, #3b82f6 ${((draftSlider + 2) / 4) * 100}%, #e5e7eb ${((draftSlider + 2) / 4) * 100}%, #e5e7eb 100%)`
                  }}
                />
              </div>

              <button
                onClick={() => quickZoom('in')}
                className="w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 active:bg-gray-300 text-gray-700 font-bold text-xl flex items-center justify-center transition-colors"
                disabled={zoom >= MAX_ZOOM}
              >
                +
              </button>
            </div>

            <div className="text-center mb-4">
              <button
                onClick={resetZoom}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium underline-offset-2 hover:underline"
              >
                איפוס לברירת מחדל (100%)
              </button>
            </div>

            <div className="flex gap-3">
              <button
                onClick={closeZoomModal}
                className="flex-1 py-2.5 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-colors"
              >
                ביטול
              </button>
              <button
                onClick={saveZoomModal}
                className="flex-1 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors shadow-sm"
              >
                שמירה
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DayView;
